/**
 * RAPID CONTACT PAGE — pagina full-page della form di contatto fiera, riusata in
 * tre modalità decise dalla rotta:
 *   - CREAZIONE (`/add-rapid-contact`, nessun id): workflow batch-friendly — dopo
 *     la creazione il form si svuota (mantenendo l'evento di riferimento), mostra
 *     un toast e l'utente resta sulla pagina per inserimenti consecutivi. Sotto il
 *     form, lista degli ultimi inseriti e dialog "evento di riferimento".
 *   - EDIT (`/rapid-contact/:id`, contatto NON importato): stessa form precompilata
 *     col record; il submit fa `updateRapidContact` e torna alla lista. Niente UX batch.
 *   - SOLA LETTURA (`/rapid-contact/:id`, contatto già importato): stessa form con
 *     tutti i campi `disabled` e nessun submit (i contatti importati sono immutabili).
 *
 * Submit: UN solo dispatch `createRapidContact`/`updateRapidContact` che persiste
 * TUTTI i campi del form in un'unica entità RapidContact (vedi RAPIDCONTACT_REACT_GUIDE
 * §13-14). Il record in dettaglio è caricato via thunk `fetchRapidContact` (fetchOne).
 *
 * Layout responsive: vedi `.rapid-contact-page` e la griglia `.cap-grid` / `.cap-left` /
 * `.cap-side` in `_capture.scss`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { CheckGlyph } from '../../components/icons';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { fetchCountries } from '../../entities/geography/geographyThunks';
import { createRapidContact, updateRapidContact, fetchRapidContact } from '../../entities/rapidContact/rapidContactThunks';
import {
  rapidContactApi,
  rapidContactTaskRulesApi,
  INTEREST_LEVELS as INTEREST_LEVEL_KEYS,
  type InterestLevel,
  type RapidContactTaskRule,
} from '../../entities/rapidContact/rapidContactApi';
import { taskPriorityFromDto } from '../../entities/task/taskMappers';
import { userApi, type Member } from '../../entities/user/userApi';
import type { ListTypeEntry } from '../../entities/listType/listTypeTypes';
import type { RapidContact } from '../../entities/rapidContact/rapidContactTypes';

/**
 * Form derivato dal tracciato record "Contatti fiere" (vedi
 * Desktop/ts/tracciato.md): 3 blocchi logici — Contatto, Azienda, Interesse/
 * Fiera. TUTTI i campi vengono persistiti in un'unica entità RapidContact dal
 * submit (`createRapidContact`). `interestLevel` è un Select string '1'..'5'
 * mappato a number prima del dispatch (0 = unset).
 */
interface RapidContactForm {
  // --- Contatto ---
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  // --- Azienda ---
  companyName: string;
  companyDomain: string;
  companyAddress: string;
  companyCountry: string;
  // --- Interesse / Fiera ---
  fairEvent: string;
  interest: string;
  productCategory: string;
  interestLevel: string;
  followupAction: string;
  /** Key di una voce della picklist dello stato di follow-up (FILO-67); '' = nessuno. */
  followupStatusListTypeEntryKey: string;
  notes: string;
  /** Commerciale di riferimento (FILO-67): sempre valorizzato, di default l'utente collegato. */
  salespersonUserId: number;
}

const INITIAL_FORM: RapidContactForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  companyName: '',
  companyDomain: '',
  companyAddress: '',
  companyCountry: '',
  fairEvent: '',
  interest: '',
  productCategory: '',
  interestLevel: '',
  followupAction: '',
  followupStatusListTypeEntryKey: '',
  notes: '',
  salespersonUserId: 0,
};

// Stato Liferay di un utente disattivato (WorkflowConstants.STATUS_INACTIVE), come in
// UsersTeamSettings: chi è disattivato non si offre fra i commerciali.
const USER_STATUS_INACTIVE = 5;

function memberName(m: Member): string {
  return `${m.firstName} ${m.lastName}`.trim() || m.email || `#${m.userId}`;
}

// Lunghezze massime dei campi di testo: le stesse dei model hints di RapidContact
// (portlet-model-hints.xml di crm-core-service), che il servizio controlla al
// salvataggio. Qui impediscono di scrivere oltre, invece di scoprirlo dal toast.
const MAX_LENGTH = { text: 255, long: 4000 } as const;

// Chiave localStorage dell'evento/fiera di riferimento: precompila `fairEvent`
// su tutti i contatti inseriti. Distinzione null (mai impostato → chiedi) vs
// "" (impostato vuoto → non richiedere più).
const EVENT_STORAGE_KEY = 'rapidContact.eventReference';

function getStoredEvent(): string | null {
  try {
    return localStorage.getItem(EVENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

// livello_interesse: punteggio 1-5 (file 2025 nel tracciato). I pulsanti nascono dall'elenco dei
// livelli allineato a InterestLevel (FILO-67): la posizione nell'elenco è il valore salvato, e ZERO,
// «non indicato», non ha un pulsante perché è l'assenza di una scelta.
const LEVEL_BUTTONS = INTEREST_LEVEL_KEYS.map((key, value) => ({ key, value })).filter((l) => l.value > 0);

// Numero di contatti recenti mostrati sotto il form.
const RECENT_LIMIT = 5;

/**
 * Destinazione del link di risalita, passata in `state` da chi apre questa pagina.
 * `labelKey` e non `label`: lo state finisce nella history del browser e sopravvive a un
 * cambio di lingua, quindi si memorizza la chiave i18n e si traduce al render.
 */
export interface BackTo {
  path: string;
  labelKey: string;
}

const DEFAULT_BACK_TO: BackTo = { path: '/rapid-contacts', labelKey: 'rapidContact.listTitle' };

export function RapidContactPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  // Modalità pagina (TS-100): senza :rapidContactId è creazione (workflow batch). Con id è
  // dettaglio della stessa form riusata — edit se il contatto è ancora da importare, oppure
  // sola lettura (readOnly) se già importato. readOnly è deciso dopo il fetch del record.
  const { rapidContactId } = useParams();
  const recordId = rapidContactId ? Number(rapidContactId) : undefined;
  const isDetail = recordId !== undefined;
  // Dove riportare l'utente: lo dichiara chi apre la pagina (`state.backTo`), perché la
  // stessa scheda si raggiunge dalla lista Fiere e dal pannello Fiere di un contatto.
  // Ripiego sulla lista Fiere quando lo state non c'è — deep link, refresh, o un ingresso
  // non ancora istruito: preferito a `navigate(-1)`, che su un accesso diretto porterebbe
  // fuori dall'applicazione e non permette di dire nell'etichetta dove si sta tornando.
  const { state } = useLocation();
  const backTo = (state as { backTo?: BackTo } | null)?.backTo ?? DEFAULT_BACK_TO;
  const [readOnly, setReadOnly] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(isDetail);
  // Evento/fiera di riferimento (solo creazione): chiesto all'apertura, precompila `fairEvent`
  // su ogni contatto (campo poi modificabile per singolo contatto).
  const [eventReference, setEventReference] = useState<string>(() => getStoredEvent() ?? '');
  const [eventDialogOpen, setEventDialogOpen] = useState<boolean>(
    () => !isDetail && getStoredEvent() === null);
  const [eventDialogValue, setEventDialogValue] = useState<string>(() => getStoredEvent() ?? '');

  // Il commerciale di riferimento parte dall'utente collegato (FILO-67). Va letto PRIMA dello
  // stato del form, che lo usa nel suo inizializzatore.
  const currentUserId = useAppSelector((s) => s.auth.user?.userId) ?? 0;

  const [form, setForm] = useState<RapidContactForm>(() => ({
    ...INITIAL_FORM,
    // In dettaglio il form viene riempito dal fetch del record; l'evento di riferimento
    // (localStorage) precompila solo in creazione.
    fairEvent: isDetail ? '' : (getStoredEvent() ?? ''),
    salespersonUserId: currentUserId,
  }));
  const [submitting, setSubmitting] = useState(false);
  // Vincoli obbligatori allineati al backend: persona (almeno uno tra firstName/lastName),
  // azienda (almeno uno tra companyName/companyDomain) e fiera/evento (fairEvent),
  // validati al submit.
  const [nameError, setNameError] = useState(false);
  const [companyError, setCompanyError] = useState(false);
  const [fairEventError, setFairEventError] = useState(false);
  // Voci della picklist dello stato di follow-up (FILO-67), lette una volta all'apertura.
  const [followupStatuses, setFollowupStatuses] = useState<ListTypeEntry[]>([]);
  // Le regole dei task di follow-up della company (FILO-67), per dire sotto ogni livello che task
  // nascerà all'importazione. Lette una volta; senza, i pulsanti mostrano solo il numero.
  const [taskRules, setTaskRules] = useState<RapidContactTaskRule[]>([]);
  const firstNameRef = useRef<HTMLInputElement>(null);

  // Nazioni per l'autocomplete del campo `companyCountry`: riusa la lista
  // cached nello slice geography (stesso dato di CountrySelect). Lazy load
  // al mount; il campo resta freeSolo (stringa libera), i country sono solo
  // suggerimenti mentre si digita.
  const countries = useAppSelector((s) => s.geography.countries);
  // GroupId per il fetch diretto dei contatti rapidi recenti (vedi loadRecent).
  const groupId = useAppSelector((s) => s.auth.user?.defaultGroupId);
  // Utenti del sito, gli stessi di Impostazioni › Utenti e team: le scelte del commerciale.
  const [members, setMembers] = useState<Member[]>([]);
  // Il commerciale del record aperto, con il nome che manda il server: resta visibile anche
  // se è stato disattivato e quindi non compare fra le scelte.
  const [recordSalesperson, setRecordSalesperson] = useState<{ userId: number; name: string } | null>(null);
  const [salespersonError, setSalespersonError] = useState(false);
  // Ultimi N contatti rapidi REALMENTE a sistema (createDate desc). Letti con
  // un fetch diretto dell'API — NON da state.rapidContacts.items, che è lo
  // snapshot PAGINATO condiviso con la lista /rapid-contacts e mostrerebbe gli
  // ultimi della pagina corrente, non gli ultimi assoluti (vedi §4.7 e §10).
  const [recentContacts, setRecentContacts] = useState<RapidContact[]>([]);

  // Rilegge gli ultimi RECENT_LIMIT dal server (display-only). Chiamata al
  // mount e dopo ogni create. Errori ignorati: non devono bloccare il form.
  const loadRecent = useCallback(async () => {
    if (!groupId) return;
    try {
      const page = await rapidContactApi.getAll(groupId, {
        sort: 'createDate:desc',
        pageSize: RECENT_LIMIT,
      });
      setRecentContacts(page.items);
    } catch {
      /* lista recenti display-only: errori ignorati. */
    }
  }, [groupId]);

  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);
  // Utenti del sito per il commerciale. Un errore non blocca il form: la combo resta con
  // l'utente collegato o con il commerciale del record, e il servizio controlla comunque.
  useEffect(() => {
    if (!groupId) return;
    let active = true;
    userApi.getGroupMembers(groupId)
      .then((list) => { if (active) setMembers(list); })
      .catch(() => { /* scelte del commerciale non disponibili: non bloccante. */ });
    return () => { active = false; };
  }, [groupId]);
  // Voci dello stato di follow-up. Un errore non blocca il form: la tendina resta vuota e il
  // contatto si salva senza stato, che è facoltativo.
  useEffect(() => {
    let active = true;
    rapidContactApi.followupStatuses()
      .then((list) => { if (active) setFollowupStatuses(list); })
      .catch(() => { /* voci non disponibili: non bloccante. */ });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    rapidContactTaskRulesApi.get()
      .then((r) => { if (active) setTaskRules(r.rules); })
      .catch(() => { /* regole non disponibili: i pulsanti restano col solo numero. */ });
    return () => { active = false; };
  }, []);
  // L'utente collegato arriva dallo store, che al primo render può non essere pronto.
  useEffect(() => {
    if (!isDetail && currentUserId > 0)
      setForm((f) => (f.salespersonUserId > 0 ? f : { ...f, salespersonUserId: currentUserId }));
  }, [isDetail, currentUserId]);
  // Lista "ultimi inseriti": solo in creazione (UX batch).
  useEffect(() => {
    if (isDetail) return;
    loadRecent();
  }, [isDetail, loadRecent]);

  // Dettaglio: carica il record da editare/visualizzare (thunk fetchOne → popola byId)
  // e imposta readOnly se già importato.
  useEffect(() => {
    if (recordId === undefined) return;
    let active = true;
    setLoadingRecord(true);
    dispatch(fetchRapidContact(recordId)).unwrap()
      .then((rc) => {
        if (!active) return;
        setForm({
          firstName: rc.firstName ?? '',
          lastName: rc.lastName ?? '',
          email: rc.email ?? '',
          phone: rc.phone ?? '',
          role: rc.role ?? '',
          companyName: rc.companyName ?? '',
          companyDomain: rc.companyDomain ?? '',
          companyAddress: rc.companyAddress ?? '',
          companyCountry: rc.companyCountry ?? '',
          fairEvent: rc.fairEvent ?? '',
          interest: rc.interest ?? '',
          productCategory: rc.productCategory ?? '',
          interestLevel: rc.interestLevel ? String(rc.interestLevel) : '',
          followupAction: rc.followupAction ?? '',
          followupStatusListTypeEntryKey: rc.followupStatusListTypeEntryKey ?? '',
          notes: rc.notes ?? '',
          salespersonUserId: rc.salespersonUserId > 0 ? rc.salespersonUserId : currentUserId,
        });
        setRecordSalesperson(rc.salespersonUserId > 0
          ? { userId: rc.salespersonUserId, name: rc.salespersonUserName || `#${rc.salespersonUserId}` }
          : null);
        setReadOnly(rc.imported);
      })
      .catch((err) => {
        if (!active) return;
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        navigate(backTo.path);
      })
      .finally(() => { if (active) setLoadingRecord(false); });
    return () => { active = false; };
    // `backTo.path` è una stringa (non l'oggetto, che cambia identità a ogni render):
    // stabile finché non si naviga davvero, quindi non rilancia il fetch.
  }, [recordId, dispatch, navigate, toast, backTo.path, currentUserId]);
  // Commerciali proponibili: i membri attivi del sito, per nome; in testa il commerciale del
  // record se non è fra questi (disattivato), così il campo non resta vuoto.
  const salespersonOptions = useMemo(() => {
    const options = members
      .filter((m) => m.status !== USER_STATUS_INACTIVE)
      .map((m) => ({ userId: m.userId, name: memberName(m) }))
      .sort((a, b) => a.name.localeCompare(b.name));
    if (recordSalesperson && !options.some((o) => o.userId === recordSalesperson.userId))
      options.unshift(recordSalesperson);
    return options;
  }, [members, recordSalesperson]);

  const countryOptions = useMemo(
    () => countries.map((c) => c.title),
    [countries],
  );

  // Stati di follow-up proponibili: le voci attive della picklist; quella già assegnata al
  // contatto resta fra le scelte anche se il cliente l'ha disattivata, così il campo non si svuota.
  const followupStatusOptions = useMemo(
    () => followupStatuses.filter((e) => e.active || e.key === form.followupStatusListTypeEntryKey),
    [followupStatuses, form.followupStatusListTypeEntryKey],
  );

  // Apre il dialog evento precompilando con il riferimento corrente (per
  // permettere la modifica dell'evento di riferimento in un secondo momento).
  function openEventDialog() {
    setEventDialogValue(eventReference);
    setEventDialogOpen(true);
  }

  // Conferma l'evento: lo memorizza (localStorage) e precompila il campo
  // fairEvent del form corrente. Valore vuoto ammesso (= nessun evento).
  function confirmEventDialog() {
    const value = eventDialogValue.trim();
    setEventReference(value);
    try {
      localStorage.setItem(EVENT_STORAGE_KEY, value);
    } catch {
      /* storage non disponibile: l'evento vale solo per questa sessione. */
    }
    setForm((f) => ({ ...f, fairEvent: value }));
    setEventDialogOpen(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (readOnly) return; // sola lettura: nessun submit.
    // Almeno uno tra firstName/lastName e almeno uno tra companyName/companyDomain.
    const nameMissing = !form.firstName.trim() && !form.lastName.trim();
    const companyMissing = !form.companyName.trim() && !form.companyDomain.trim();
    const fairEventMissing = !form.fairEvent.trim();
    const salespersonMissing = !(form.salespersonUserId > 0);
    setNameError(nameMissing);
    setCompanyError(companyMissing);
    setFairEventError(fairEventMissing);
    setSalespersonError(salespersonMissing);
    if (nameMissing || companyMissing || fairEventMissing || salespersonMissing) return;
    setSubmitting(true);

    // Stringhe vuote → null (campo svuotato); interestLevel Select '1'..'5' → number (0 = unset).
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      role: form.role.trim() || null,
      companyName: form.companyName.trim() || null,
      companyDomain: form.companyDomain.trim() || null,
      companyAddress: form.companyAddress.trim() || null,
      companyCountry: form.companyCountry.trim() || null,
      fairEvent: form.fairEvent.trim() || null,
      interest: form.interest.trim() || null,
      productCategory: form.productCategory.trim() || null,
      interestLevel: Number(form.interestLevel) || 0,
      followupAction: form.followupAction.trim() || null,
      // '' e non null: nel PATCH un null vale «lascia com'è», la stringa vuota toglie lo stato.
      followupStatusListTypeEntryKey: form.followupStatusListTypeEntryKey,
      notes: form.notes.trim() || null,
      salespersonUserId: form.salespersonUserId,
    };

    try {
      // Dettaglio (edit): update del record e ritorno a DOVE SI ERA (lista Fiere o scheda
      // contatto, vedi backTo). Nessun workflow batch.
      if (isDetail && recordId !== undefined) {
        await dispatch(updateRapidContact({ rapidContactId: recordId, ...payload })).unwrap();
        toast({ severity: 'success', message: t('rapidContact.updated') });
        navigate(backTo.path);
        return;
      }

      // Creazione: UN solo dispatch che persiste tutti i campi in un'unica entità
      // (transazionale lato backend). .unwrap() per intercettare l'errore inline (guida §14).
      await dispatch(createRapidContact(payload)).unwrap();

      // Aggiorna la lista "ultimi inseriti" rileggendo dal server (fire-and-forget).
      void loadRecent();

      // Successo: toast + reset form (mantenendo l'evento di riferimento
      // precompilato) + focus a firstName per l'inserimento successivo.
      toast({ severity: 'success', message: t('rapidContact.success') });
      setForm({ ...INITIAL_FORM, fairEvent: eventReference, salespersonUserId: currentUserId });
      firstNameRef.current?.focus();
    } catch (err) {
      // unwrap() ha lanciato: errore già normalizzato da extractError (guida §8).
      // Il form resta compilato così l'utente può correggere senza riscrivere.
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  }

  const level = Number(form.interestLevel) || 0;

  // La regola del livello in breve, per il pulsante: «Alta · 2 gg», «Alta · oggi», «nessun task».
  function ruleLabel(key: InterestLevel): string | null {
    const rule = taskRules.find((r) => r.level === key);
    if (!rule) return null;
    if (!rule.task) return t('rapidContact.levelRule.noTask');
    const when = rule.days === 0 ? t('rapidContact.levelRule.today') : t('rapidContact.levelRule.days', { count: rule.days });
    return `${t(`task.priorities.${taskPriorityFromDto(rule.priority)}`)} · ${when}`;
  }

  return (
    <div className="rapid-contact-page">
      {/* Risalita in alto a sinistra, stesso `detail-back-link` delle schede
          Contatto/Azienda/Preventivo. C'è SEMPRE, anche in sola lettura dove non ci sono
          bottoni. Punta a DOVE SI ERA: la scheda si raggiunge sia dalla lista Fiere sia
          dal pannello Fiere di un contatto, e chi ci porta lo dichiara in `state.backTo`. */}
      <Link to={backTo.path} className="text-sm text-primary detail-back-link">
        &larr; {t(backTo.labelKey)}
      </Link>

      <div className="page-head">
        <div>
          <h1 className="page-title">
            {isDetail
              ? (readOnly ? t('rapidContact.viewTitle') : t('rapidContact.editTitle'))
              : t('rapidContact.title')}
          </h1>
          <span className="page-sub">
            {isDetail
              ? (readOnly ? t('rapidContact.readonlyHint') : t('rapidContact.editDescription'))
              : t('rapidContact.description')}
          </span>
        </div>
      </div>

      <div className="cap-grid">
        {loadingRecord ? (
          <p className="list-message">{t('common.loading')}</p>
        ) : (
        <form onSubmit={handleSubmit} className="cap-left">
          {/* Contatto e Azienda affiancati: due anagrafiche brevi che si
              compilano in parallelo. Interesse/Fiera resta sotto, a tutta
              larghezza, perche' ha i campi lunghi. */}
          <div className="blk-pair">
            <section className="blk">
              <div className="blk__head">
                <span className="blk__n">1</span>
                <h3 className="blk__title">{t('rapidContact.sectionContact')}</h3>
              </div>
              <p className="blk__hint">{t('rapidContact.validation.nameRequired')}</p>
              <div className="blk__body">
                <div className="cap-row">
                  <div className={`cf${nameError ? ' is-error' : ''}`}>
                    <label htmlFor="rc-first">{t('person.firstName')}<span className="req">*</span></label>
                    <input
                      id="rc-first" disabled={readOnly} maxLength={MAX_LENGTH.text}
                      ref={firstNameRef}
                      autoFocus
                      value={form.firstName}
                      onChange={(e) => { setForm({ ...form, firstName: e.target.value }); if (nameError) setNameError(false); }}
                    />
                  </div>
                  <div className={`cf${nameError ? ' is-error' : ''}`}>
                    <label htmlFor="rc-last">{t('person.lastName')}<span className="req">*</span></label>
                    <input
                      id="rc-last" disabled={readOnly} maxLength={MAX_LENGTH.text}
                      value={form.lastName}
                      onChange={(e) => { setForm({ ...form, lastName: e.target.value }); if (nameError) setNameError(false); }}
                    />
                    {nameError && <span className="cf__error">{t('rapidContact.validation.nameRequired')}</span>}
                  </div>
                </div>
                <div className="cap-row">
                  <div className="cf">
                    <label htmlFor="rc-email">{t('common.email')}</label>
                    <input id="rc-email" disabled={readOnly} maxLength={MAX_LENGTH.text} type="email" placeholder="nome@azienda.it"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="cf">
                    <label htmlFor="rc-phone">{t('common.phone')}</label>
                    <input id="rc-phone" disabled={readOnly} maxLength={MAX_LENGTH.text} type="tel" placeholder="+39 …"
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div className="cf">
                  <label htmlFor="rc-role">{t('rapidContact.role')}</label>
                  <input id="rc-role" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })} />
                </div>
              </div>
            </section>

            <section className="blk">
              <div className="blk__head">
                <span className="blk__n">2</span>
                <h3 className="blk__title">{t('rapidContact.sectionCompany')}</h3>
              </div>
              <p className="blk__hint">{t('rapidContact.validation.companyRequired')}</p>
              <div className="blk__body">
                <div className="cap-row">
                  <div className={`cf${companyError ? ' is-error' : ''}`}>
                    <label htmlFor="rc-co">{t('rapidContact.companyName')}<span className="req">*</span></label>
                    <input id="rc-co" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.companyName}
                      onChange={(e) => { setForm({ ...form, companyName: e.target.value }); if (companyError) setCompanyError(false); }} />
                  </div>
                  <div className={`cf${companyError ? ' is-error' : ''}`}>
                    <label htmlFor="rc-dom">{t('rapidContact.companyDomain')}<span className="req">*</span></label>
                    <input id="rc-dom" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.companyDomain}
                      onChange={(e) => { setForm({ ...form, companyDomain: e.target.value }); if (companyError) setCompanyError(false); }} />
                    {companyError && <span className="cf__error">{t('rapidContact.validation.companyRequired')}</span>}
                  </div>
                </div>
                <div className="cf">
                  <label htmlFor="rc-addr">{t('rapidContact.companyAddress')}</label>
                  <input id="rc-addr" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.companyAddress}
                    onChange={(e) => setForm({ ...form, companyAddress: e.target.value })} />
                </div>
                {/* Nazione: input con datalist invece dell'Autocomplete MUI —
                    stesso comportamento free-solo con suggerimenti, ma il campo
                    resta un `<input>` come tutti gli altri del modulo. */}
                <div className="cf">
                  <label htmlFor="rc-country">{t('rapidContact.companyCountry')}</label>
                  <input id="rc-country" disabled={readOnly} maxLength={MAX_LENGTH.text} list="rc-countries" value={form.companyCountry}
                    onChange={(e) => setForm({ ...form, companyCountry: e.target.value })} />
                  <datalist id="rc-countries">
                    {countryOptions.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
            </section>
          </div>

          <section className="blk">
            <div className="blk__head">
              <span className="blk__n">3</span>
              <h3 className="blk__title">{t('rapidContact.sectionFair')}</h3>
              {/* "Cambia evento": UX batch, solo in creazione. */}
              {!isDetail && (
                <button type="button" className="blk__link" onClick={openEventDialog}>
                  {t('rapidContact.changeEvent')}
                </button>
              )}
            </div>
            <div className="blk__body">
              <div className={`cf${fairEventError ? ' is-error' : ''}`}>
                <label htmlFor="rc-fair">{t('rapidContact.fairEvent')}</label>
                <input id="rc-fair" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.fairEvent}
                  onChange={(e) => { setForm({ ...form, fairEvent: e.target.value }); if (fairEventError) setFairEventError(false); }} />
                {fairEventError && <span className="cf__error">{t('rapidContact.validation.fairEventRequired')}</span>}
              </div>
              <div className="cf">
                <label htmlFor="rc-interest">{t('rapidContact.interest')}</label>
                <textarea id="rc-interest" disabled={readOnly} maxLength={MAX_LENGTH.long} value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })} />
              </div>
              <div className="cf">
                <label htmlFor="rc-cat">{t('rapidContact.productCategory')}</label>
                <input id="rc-cat" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.productCategory}
                  onChange={(e) => setForm({ ...form, productCategory: e.target.value })} />
              </div>

              {/* Livello di interesse come barra tappabile: a una fiera si
                  sceglie con un dito, non aprendo una tendina. I gradini fino
                  al valore scelto restano pieni, cosi' si legge come livello. */}
              <div className="cf">
                <div className="cf-lvl__top">
                  <label>{t('rapidContact.interestLevel')}</label>
                  {level > 0 && <span className="lvl-tag">{level} / {LEVEL_BUTTONS.length}</span>}
                </div>
                <div className="heat" role="group" aria-label={t('rapidContact.interestLevel')}>
                  {LEVEL_BUTTONS.map(({ key, value: lvl }) => (
                    <button
                      key={key}
                      type="button"
                      disabled={readOnly}
                      aria-pressed={level === lvl}
                      className={`heat__p${lvl <= level ? ' fill' : ''}${lvl === level ? ' sel' : ''}`}
                      onClick={() => setForm({ ...form, interestLevel: String(lvl) })}
                    >
                      <span className="heat__n">{lvl}</span>
                      {ruleLabel(key) && <span className="heat__rule">{ruleLabel(key)}</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Commerciale di riferimento (FILO-67): tendina degli utenti del sito, reso come
                  gli altri campi del modulo. Obbligatorio, di default l'utente collegato. */}
              <div className={`cf${salespersonError ? ' is-error' : ''}`}>
                <label htmlFor="rc-salesperson">{t('rapidContact.salesperson')}</label>
                <select
                  id="rc-salesperson" disabled={readOnly}
                  value={form.salespersonUserId > 0 ? String(form.salespersonUserId) : ''}
                  onChange={(e) => {
                    setForm({ ...form, salespersonUserId: Number(e.target.value) || 0 });
                    if (salespersonError) setSalespersonError(false);
                  }}
                >
                  {!(form.salespersonUserId > 0) && <option value="" />}
                  {/* L'utente scelto c'è sempre fra le opzioni, anche prima che la lista arrivi. */}
                  {form.salespersonUserId > 0 && !salespersonOptions.some((o) => o.userId === form.salespersonUserId) && (
                    <option value={String(form.salespersonUserId)}>
                      {recordSalesperson?.userId === form.salespersonUserId ? recordSalesperson.name : '…'}
                    </option>
                  )}
                  {salespersonOptions.map((o) => (
                    <option key={o.userId} value={String(o.userId)}>{o.name}</option>
                  ))}
                </select>
                {salespersonError && <span className="cf__error">{t('rapidContact.validation.salespersonRequired')}</span>}
              </div>

              {/* Stato follow-up (FILO-67): tendina delle voci della picklist, gestibili dal
                  cliente in Gestione picklist. Facoltativo: la voce vuota lo toglie. */}
              <div className="cf">
                <label htmlFor="rc-fustatus">{t('rapidContact.followupStatus')}</label>
                <select
                  id="rc-fustatus" disabled={readOnly}
                  value={form.followupStatusListTypeEntryKey}
                  onChange={(e) => setForm({ ...form, followupStatusListTypeEntryKey: e.target.value })}
                >
                  <option value="" />
                  {/* Una key che la picklist non ha più (voce cancellata): resta visibile così. */}
                  {form.followupStatusListTypeEntryKey !== ''
                    && !followupStatusOptions.some((e) => e.key === form.followupStatusListTypeEntryKey) && (
                    <option value={form.followupStatusListTypeEntryKey}>{form.followupStatusListTypeEntryKey}</option>
                  )}
                  {followupStatusOptions.map((e) => <option key={e.key} value={e.key}>{e.name}</option>)}
                </select>
              </div>

              <div className="cf">
                <label htmlFor="rc-fu">{t('rapidContact.followupAction')}</label>
                <input id="rc-fu" disabled={readOnly} maxLength={MAX_LENGTH.text} value={form.followupAction}
                  onChange={(e) => setForm({ ...form, followupAction: e.target.value })} />
              </div>
              <div className="cf">
                <label htmlFor="rc-notes">{t('rapidContact.notes')}</label>
                <textarea id="rc-notes" disabled={readOnly} maxLength={MAX_LENGTH.long} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Bottoni solo in editing (modifica o creazione): in sola lettura non c'è nulla da
              confermare o annullare, e la risalita è affidata al link in testata. */}
          {!readOnly && (
            <div className="cap-bar">
              <button type="button" className="cap-btn cap-btn--ghost" disabled={submitting}
                onClick={() => navigate(backTo.path)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="cap-btn cap-btn--primary" disabled={submitting}>
                <CheckGlyph />
                {/* In creazione il flusso è batch — si salva e si riparte con un contatto
                    nuovo — quindi l'etichetta lo dice. In modifica si salva e basta. */}
                {isDetail ? t('common.save') : t('rapidContact.saveAndNew')}
              </button>
            </div>
          )}
        </form>
        )}

        {/* Ultimi contatti rapidi a sistema (fetch diretto API, createDate desc — vedi
            loadRecent). Solo in creazione (UX batch): nel concept stanno in colonna, come
            conferma che il salvataggio ha funzionato senza dover lasciare il modulo. */}
        {!isDetail && recentContacts.length > 0 && (
          <aside className="cap-side">
            <h3 className="cap-side__title">{t('rapidContact.recentTitle')}</h3>
            <div className="rc2">
              {recentContacts.map((c) => {
                const full = `${c.firstName} ${c.lastName}`.trim();
                const initials = (full || c.email || '?')
                  .split(/[\s.@]+/).filter(Boolean).slice(0, 2)
                  .map((w) => w[0]?.toUpperCase() ?? '').join('');
                return (
                  <div key={c.rapidContactId} className="rc2item">
                    <span className="rc2av">{initials}</span>
                    <span className="rc2main">
                      <span className="rc2name">{full || c.email}</span>
                      {c.email && <span className="rc2email">{c.email}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </aside>
        )}
      </div>

      {/* Dialog evento di riferimento (solo creazione): chiesto all'apertura (se mai
          impostato) o riapribile via "Cambia evento". Precompila fairEvent sui contatti. */}
      {!isDetail && (
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('rapidContact.eventDialogTitle')}</DialogTitle>
        <DialogContent>
          {/* sx invece della utility .mb-*: MUI inietta i suoi stili via
              emotion e sovrascriverebbe la classe SCSS. sx genera una classe
              con specificità adeguata. */}
          <DialogContentText sx={{ mb: 2 }}>
            {t('rapidContact.eventDialogDescription')}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label={t('rapidContact.fairEvent')}
            value={eventDialogValue}
            onChange={(e) => setEventDialogValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmEventDialog();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmEventDialog} variant="contained">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      )}
    </div>
  );
}
